import type { Wallet } from '../types';

const round2 = (x: number) => Math.round(x * 100) / 100;

/** In-memory demo wallet with optional localStorage persistence (guarded: storage may be blocked). */
export class DemoWallet implements Wallet {
  private balance: number;
  private readonly key: string | null;

  constructor(initial: number, storageKey: string | null) {
    this.key = storageKey ? `${storageKey}:balance` : null;
    const stored = this.load();
    this.balance = round2(stored ?? initial);
  }

  private load(): number | null {
    if (!this.key) return null;
    try {
      const raw = globalThis.localStorage?.getItem(this.key);
      const n = raw == null ? NaN : Number(raw);
      return Number.isFinite(n) && n >= 0 ? n : null;
    } catch {
      return null;
    }
  }

  private save(): void {
    if (!this.key) return;
    try {
      globalThis.localStorage?.setItem(this.key, String(this.balance));
    } catch {
      /* storage unavailable (private mode, partitioned iframe) — keep in memory */
    }
  }

  async getBalance(): Promise<number> {
    return this.balance;
  }

  async debit(amount: number): Promise<number> {
    if (amount > this.balance + 1e-9) throw new Error('INSUFFICIENT_FUNDS');
    this.balance = round2(this.balance - amount);
    this.save();
    return this.balance;
  }

  async credit(amount: number): Promise<number> {
    this.balance = round2(this.balance + amount);
    this.save();
    return this.balance;
  }

  setBalance(balance: number): void {
    this.balance = round2(Math.max(0, balance));
    this.save();
  }
}
